import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BookOpen, Plus, Trash2, Pencil, RotateCcw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { modeLabel } from "@/lib/constants";
import { pageHead } from "@/lib/seo";

export const Route = createFileRoute("/my-books")({
  head: () => pageHead({ path: "/my-books", title: "My books — Book Swap", description: "Manage the books you've listed on Book Swap and the requests they've received.", noindex: true }),
  component: MyBooks,
});

type Row = {
  id: string; title: string; author: string; cover_url: string | null;
  status: string; category: string; exchange_duration: string; exchange_mode: string | null;
};

type Req = {
  id: string; book_id: string; status: string; message: string | null; created_at: string;
  profiles: { full_name: string | null } | null;
};

function MyBooks() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [books, setBooks] = useState<Row[]>([]);
  const [requests, setRequests] = useState<Req[]>([]);

  useEffect(() => { if (!authLoading && !user) navigate({ to: "/login" }); }, [authLoading, user, navigate]);

  async function load() {
    if (!user) return;
    const { data: b } = await supabase.from("books")
      .select("id,title,author,cover_url,status,category,exchange_duration,exchange_mode")
      .eq("user_id", user.id).order("created_at", { ascending: false });
    setBooks(b ?? []);
    const ids = (b ?? []).map((x) => x.id);
    if (ids.length) {
      const { data: r } = await supabase.from("exchange_requests")
        .select("id,book_id,requester_id,status,message,created_at")
        .in("book_id", ids).order("created_at", { ascending: false });
      const reqs = r ?? [];
      const requesterIds = Array.from(new Set(reqs.map((x: any) => x.requester_id)));
      let nameById = new Map<string, string | null>();
      if (requesterIds.length) {
        const { data: profs } = await supabase
          .from("public_profiles")
          .select("id, full_name")
          .in("id", requesterIds);
        nameById = new Map((profs ?? []).map((p: any) => [p.id, p.full_name]));
      }
      setRequests(reqs.map((x: any) => ({ ...x, profiles: { full_name: nameById.get(x.requester_id) ?? null } })) as any);
    }
  }
  useEffect(() => { load(); }, [user]);

  async function deleteBook(id: string) {
    const { error } = await supabase.from("books").delete().eq("id", id);
    if (error) { console.error(error); return toast.error("Could not remove this listing."); }
    toast.success("Listing removed");
    load();
  }

  async function relist(id: string) {
    const { error } = await supabase.from("books").update({ status: "available" }).eq("id", id);
    if (error) { console.error(error); return toast.error("Could not relist this book."); }
    toast.success("Back on Book Swap — it's available again.");
    load();
  }

  async function setRequestStatus(id: string, status: "accepted" | "declined") {
    const { error } = await supabase.from("exchange_requests").update({ status }).eq("id", id);
    if (error) { console.error(error); return toast.error("Could not update this request."); }
    toast.success(`Request ${status}`);
    load();
  }

  if (authLoading || !user) return <div className="min-h-screen bg-background"><Navbar /></div>;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-5xl px-4 py-10">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">My books</h1>
            <p className="text-sm text-muted-foreground">{books.length} listed</p>
          </div>
          <div className="flex gap-2">
            <Link to="/history"><Button variant="outline">History</Button></Link>
            <Link to="/add-book"><Button className="bg-primary hover:bg-primary/90"><Plus className="mr-1 h-4 w-4" />Add a book</Button></Link>
          </div>
        </div>

        {books.length === 0 ? (
          <div className="mt-12 rounded-2xl bg-card p-10 text-center shadow-[var(--shadow-card)]">
            <BookOpen className="mx-auto h-10 w-10 text-primary/50" />
            <h3 className="mt-3 text-lg font-semibold">No listings yet</h3>
            <p className="mt-1 text-sm text-muted-foreground">List your first book — it takes 30 seconds.</p>
            <Link to="/add-book"><Button className="mt-5 bg-primary hover:bg-primary/90">Add a book</Button></Link>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {books.map((b) => {
              const reqs = requests.filter((r) => r.book_id === b.id && r.status === "pending");
              return (
                <div key={b.id} className="rounded-2xl bg-card p-4 shadow-[var(--shadow-card)]">
                  <div className="flex gap-4">
                    <Link to="/book/$id" params={{ id: b.id }} className="block h-24 w-16 shrink-0 overflow-hidden rounded-lg bg-primary-soft">
                      {b.cover_url ? <img src={b.cover_url} alt={b.title} className="h-full w-full object-cover" />
                        : <div className="flex h-full items-center justify-center"><BookOpen className="h-6 w-6 text-primary/40" /></div>}
                    </Link>
                    <div className="flex-1">
                      <Link to="/book/$id" params={{ id: b.id }} className="font-semibold hover:text-primary">{b.title}</Link>
                      <p className="text-sm text-muted-foreground">by {b.author}</p>
                      <div className="mt-1 flex flex-wrap gap-2 text-xs">
                        <span className="rounded-full bg-primary-soft px-2 py-0.5 text-primary">{b.category}</span>
                        <span className="rounded-full bg-accent/30 px-2 py-0.5 text-primary-dark">{modeLabel(b.exchange_mode)}</span>
                        {b.exchange_mode === "lend" && (
                          <span className="rounded-full bg-accent/30 px-2 py-0.5 text-primary-dark">{b.exchange_duration}</span>
                        )}
                        <span className="rounded-full bg-muted px-2 py-0.5 text-muted-foreground">
                          {b.status === "available" ? "Available" : "Not available for now"}
                        </span>
                      </div>
                      {b.status !== "available" && (
                        <Button size="sm" variant="outline" className="mt-3" onClick={() => relist(b.id)}>
                          <RotateCcw className="mr-1 h-3.5 w-3.5" /> Mark as available again
                        </Button>
                      )}
                    </div>
                    <div className="flex shrink-0 flex-col gap-1">
                      <Link to="/edit-book/$id" params={{ id: b.id }}>
                        <Button variant="ghost" size="sm" aria-label="Edit listing"><Pencil className="h-4 w-4" /></Button>
                      </Link>
                      <Button variant="ghost" size="sm" onClick={() => deleteBook(b.id)} className="text-destructive hover:bg-destructive/10" aria-label="Delete listing">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {reqs.length > 0 && (
                    <div className="mt-4 border-t border-border pt-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pending requests ({reqs.length})</p>
                      <div className="mt-2 space-y-2">
                        {reqs.map((r) => (
                          <div key={r.id} className="flex items-start justify-between gap-3 rounded-lg bg-primary-soft/50 p-3">
                            <div className="flex-1">
                              <p className="text-sm font-medium">{r.profiles?.full_name ?? "A reader"}</p>
                              {r.message && <p className="mt-1 text-sm text-muted-foreground">"{r.message}"</p>}
                              <p className="mt-1 text-xs text-muted-foreground">Status: {r.status}</p>
                            </div>
                            <div className="flex gap-2">
                              <Link to="/requests/$id" params={{ id: r.id }}>
                                <Button size="sm" className="bg-primary hover:bg-primary/90">
                                  {r.status === "pending" ? "Review request" : "View"}
                                </Button>
                              </Link>
                              {r.status === "pending" && (
                                <Button size="sm" variant="outline" onClick={() => setRequestStatus(r.id, "declined")}>Decline</Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
